<template>
  <div class="dual-track-container">
    <a-card :bordered="false" class="header-card">
      <div class="header-top">
        <div class="page-title">双轨预警报警算法<span class="beta-tag">beta</span></div>
        <a-button v-if="returnFullPath" type="link" class="btn-back" @click="goBackToSource">
          <a-icon type="left" />
          返回
        </a-button>
      </div>
      <div class="page-description">
        双轨算法主要针对于夜间流量的预警报警，就像给片区的夜间用水量做 “健康体检” 一样。此算法采用滑动窗口动态计算模式，创新打造快轨 + 慢轨双维度流量预警体系：快轨聚焦大偏差异常流量数据监测，可快速识别突发爆管等紧急工况；
慢轨注重流量变化趋势分析，精准捕捉连续缓慢增长等暗漏潜在信号。算法具备强抗干扰特性，从技术层面平衡预警灵敏度与误报率，彻底解决二者难以兼顾的行业痛点。
        <a-tooltip
          placement="top"
          :title="algorithmExplainTooltip"
          :overlay-style="{ maxWidth: '730px', whiteSpace: 'pre-line', fontSize: '12px', padding: '12px 16px', lineHeight: 1.6 }"
        >
          <a-icon type="question-circle" class="algorithm-tip-icon" />
        </a-tooltip>
        
      </div>
      <div class="filter-wrapper">
        <div class="filter-item">
          <span class="filter-label"><span class="required">*</span>分区选择:</span>
          <a-tree-select
            :tree-data="zoneTreeData"
            v-model="dualTrackQuery.zoneId"
            style="width: 240px"
            :replace-fields="{ title: 'zoneName', key: 'id', value: 'id', selectable: 'selectable' }"
            :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
            placeholder="请选择分区"
            allow-clear
            show-search
            treeNodeFilterProp="title"
            @change="onZoneChange"
          />
        </div>
        <div class="filter-item">
          <span class="filter-label">查询时间:</span>
          <a-range-picker
            v-model="dualTrackQuery.timeRange"
            :placeholder="['开始日期', '结束日期']"
            format="YYYY-MM-DD"
            style="width: 280px"
            :disabledDate="disabledDate"
          />
        </div>
        <div class="filter-actions">
          <a-button type="primary" class="btn-query" @click="queryDualTrackData">查询</a-button>
          <a-button class="btn-reset" @click="resetQuery">重置</a-button>
          <a-button
            type="primary"
            class="btn-export"
            @click="displayMode === 'chart' ? exportDualTrackChart() : exportDualTrackTable()"
            >导出</a-button
          >
        </div>
      </div>
    </a-card>

    <a-card :bordered="false" class="content-card">
      <div class="content-header">
        <div class="mode-switch">
          <a-tooltip :title="displayMode === 'chart' ? '切换到列表' : '切换到图表'">
            <div class="action-icon-btn" @click="toggleDisplayMode">
              <a-icon :type="displayMode === 'chart' ? 'unordered-list' : 'line-chart'" />
            </div>
          </a-tooltip>
        </div>
      </div>

      <div class="dual-track-content">
        <div v-show="displayMode === 'chart'" ref="dualTrackChart" class="chart-container"></div>

        <a-table
          v-show="displayMode === 'table'"
          :columns="dualTrackTableColumns"
          :data-source="dualTrackTableData"
          :pagination="{ pageSize: 10 }"
          :scroll="{ x: 1200 }"
          bordered
          rowKey="key"
          size="middle"
        >
          <span slot="alarmType" slot-scope="text, record" class="alarm-type-cell">
            <a-tag color="#f50" v-if="record.aramNum === 0">突发告警</a-tag>
            <a-tag color="#fa8c16" v-else-if="record.aramNum === 1">趋势预警</a-tag>
            <a-tag color="#999" v-else-if="record.aramNum === 2">数据中断</a-tag>
            <a-tag class="tag-data-missing" v-if="record.dateMissing">部分仪表数据缺失</a-tag>
          </span>
        </a-table>
      </div>
    </a-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      algorithmExplainTooltip: `双轨预警报警算法逻辑解释：      
      第一步：算出当天的夜间流量平均值。
      第二步：根据过去30天正常的夜间流量平均值，取中位数得到基线，代表日常正常夜间用水量。
      第三步：结合统计学算出用水波动的合理范围也就是标准差。（标准差=残差的中位数×1.4826，残差=夜间流量平均值-基线）
      第四步：突发告警判断规则：当天"夜间流量平均值"减去"基线" 大于"标准差"的3.5倍，则触发告警。
      第五步：趋势预警判断规则：过去连续七天"夜间流量平均值 - 基线"≥标准差，就触发预警（说明近期用水波动偏大，要留意）`,
      zoneTreeData: [],
      currentZoneName: '',
      needDefaultQuery: false, // 是否需要默认查询
      dualTrackQuery: {
        zoneId: '',
        timeRange: []
      },
      displayMode: 'chart', // chart: 图表模式, table: 列表模式
      dualTrackChartInstance: null,
      dualTrackData: [],
      dualTrackTableData: [],
      returnFullPath: '',
      returnPath: '',
      restoreState: '',
      dualTrackTableColumns: [
        {
          title: '序号',
          dataIndex: 'index',
          key: 'index',
          width: 60,
          align: 'center'
        },
        {
          title: '日期',
          dataIndex: 'date',
          key: 'date',
          width: 110,
          align: 'center'
        },
        {
          title: '3点',
          dataIndex: 'three',
          key: 'three',
          width: 80,
          align: 'center'
        },
        {
          title: '4点',
          dataIndex: 'four',
          key: 'four',
          width: 80,
          align: 'center'
        },
        {
          title: '5点',
          dataIndex: 'five',
          key: 'five',
          width: 80,
          align: 'center'
        },
        {
          title: '夜间流量平均值',
          dataIndex: 'nightAvg',
          key: 'nightAvg',
          width: 100,
          align: 'center'
        },
        {
          title: '基线',
          dataIndex: 'baseLine',
          key: 'baseLine',
          width: 80,
          align: 'center'
        },
        {
          title: '报警识别',
          dataIndex: 'alarmType',
          key: 'alarmType',
          width: 150,
          align: 'center',
          scopedSlots: { customRender: 'alarmType' }
        }
      ]
    }
  },
  created() {
    this.getZoneData()
    this.returnFullPath = this.$route.query.returnFullPath || ''
    this.returnPath = this.$route.query.returnPath || ''
    this.restoreState = this.$route.query.restoreState || ''
    // 从路由参数中获取分区和时间
    const hasRouteQuery = this.$route.query && Object.keys(this.$route.query).length > 0
    if (hasRouteQuery) {
      if (this.$route.query.zoneId) {
        this.dualTrackQuery.zoneId = this.$route.query.zoneId
        this.currentZoneName = this.$route.query.zoneName
      }
      if (this.$route.query.startTime && this.$route.query.endTime) {
        const moment = require('moment')
        const today = moment().endOf('day')
        let endTime = moment(this.$route.query.endTime)
        // 如果 endTime 超过今天，重置为今天
        if (endTime.isAfter(today)) {
          endTime = today
        }
        this.dualTrackQuery.timeRange = [moment(this.$route.query.startTime), endTime]
        // 自动查询
        this.$nextTick(() => {
          this.initDualTrackChart()
          this.queryDualTrackData()
        })
      }
    } else {
      // 如果没有路由参数，设置默认查询（在 getZoneData 完成后）
      this.needDefaultQuery = true
    }
  },
  mounted() {
    this.initDualTrackChart()
  },
  beforeDestroy() {
    if (this.dualTrackChartInstance) {
      this.dualTrackChartInstance.dispose()
      this.dualTrackChartInstance = null
    }
  },
  methods: {
    goBackToSource() {
      if (this.returnPath) {
        const query = {}
        if (this.restoreState) {
          query.restoreState = this.restoreState
        }
        this.$router.push({
          path: this.returnPath,
          query
        })
        return
      }
      if (this.returnFullPath) {
        this.$router.push(this.returnFullPath)
        return
      }
      this.$router.back()
    },
    getZoneData() {
      this.$http.get('zone-metering/zone-metering').then(({ data }) => {
        this.zoneTreeData = data
        // 如果路由带了ID，初始化分区名称
        if (this.dualTrackQuery.zoneId) {
          this.updateZoneName(this.dualTrackQuery.zoneId, data)
        } else if (this.needDefaultQuery) {
          // 如果没有路由参数，设置默认查询
          this.setDefaultQuery(data)
        }
      })
    },

    // 获取第一个片区（获取最外层的第一个）
    getFirstZone(data) {
      if (!data || data.length === 0) return null
      return data[0]
    },

    // 设置默认查询（第一个片区，近一个月）
    setDefaultQuery(data) {
      const firstZone = this.getFirstZone(data)
      if (firstZone) {
        const moment = require('moment')
        // 设置第一个片区
        this.dualTrackQuery.zoneId = firstZone.id
        this.currentZoneName = firstZone.zoneName
        // 设置近一个月的时间范围（从一个月前到今天）
        const endTime = moment().endOf('day')
        const startTime = moment().subtract(1, 'month').startOf('day')
        this.dualTrackQuery.timeRange = [startTime, endTime]
        // 自动查询
        this.$nextTick(() => {
          this.initDualTrackChart()
          this.queryDualTrackData()
        })
      }
    },

    onZoneChange(value) {
      if (value === undefined || value === null || value === '') {
        this.currentZoneName = ''
        return
      }
      this.updateZoneName(value, this.zoneTreeData)
    },

    updateZoneName(id, data) {
      if (!data) return false
      const idStr = String(id)
      for (const item of data) {
        if (String(item.id) === idStr) {
          this.currentZoneName = item.zoneName
          return true
        }
        if (item.children) {
          const found = this.updateZoneName(id, item.children)
          if (found) return true
        }
      }
      return false
    },

    // 禁用未来日期
    disabledDate(current) {
      const moment = require('moment')
      // 禁用今天之后的日期
      return current && current > moment().endOf('day')
    },

    resetQuery() {
      this.dualTrackQuery = {
        zoneId: '',
        timeRange: []
      }
      this.currentZoneName = ''
      this.dualTrackData = []
      this.dualTrackTableData = []
      this.renderDualTrackChart([])
    },

    initDualTrackChart() {
      if (!this.$refs.dualTrackChart) return

      const echarts = require('echarts')

      if (this.dualTrackChartInstance) {
        this.dualTrackChartInstance.dispose()
      }

      this.dualTrackChartInstance = echarts.init(this.$refs.dualTrackChart)

      // 设置初始空图表
      const option = {
        title: {
          text: `${this.currentZoneName || ''}夜间流量分析（双轨预警算法beta）`,
          left: 'center',
          top: 0,
          textStyle: {
            fontSize: 16,
            fontWeight: 'bold',
            color: '#333'
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          }
        },
        legend: {
          data: [
            '夜间流量平均值',
            '基线',
            {
              name: '正常范围',
              icon: 'circle',
              itemStyle: {
                color: 'rgba(46, 231, 239, 0.8)'
              }
            },
            '突发告警',
            '趋势预警',
            '数据中断',
            {
              name: '部分仪表数据缺失',
              icon: 'circle',
              itemStyle: {
                color: '#FFFFFF',
                borderColor: '#FC0000',
                borderWidth: 1
              }
            }
          ],
          top: 40,
          right: 'center',
          itemGap: 25,
          itemWidth: 10,
          itemHeight: 10,
          textStyle: {
            fontSize: 14
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%', // 增加底部空间，为滑块留出位置
          top: '18%',
          containLabel: true
        },
        dataZoom: [
          {
            type: 'slider',
            show: true,
            xAxisIndex: [0],
            start: 0,
            end: 100,
            bottom: 20,
            height: 20,
            handleSize: '80%',
            handleStyle: {
              color: '#fff',
              shadowBlur: 3,
              shadowColor: 'rgba(0, 0, 0, 0.6)',
              shadowOffsetX: 2,
              shadowOffsetY: 2
            },
            textStyle: {
              color: '#666'
            },
            borderColor: '#ccc'
          },
          {
            type: 'inside',
            xAxisIndex: [0],
            start: 0,
            end: 100,
            zoomOnMouseWheel: true,
            moveOnMouseMove: true,
            moveOnMouseWheel: false
          }
        ],
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: []
        },
        yAxis: {
          type: 'value',
          name: '流量（m³）',
          nameLocation: 'center',
          nameGap: 40,
          axisLabel: {
            formatter: '{value}'
          },
          scale: false
        },
        series: [
          {
            name: '正常范围',
            type: 'custom',
            renderItem: () => null,
            data: []
          },
          {
            name: '正常范围边界',
            type: 'line',
            data: [],
            lineStyle: {
              width: 0
            },
            symbol: 'none',
            showInLegend: false
          },
          {
            name: '正常范围边界上',
            type: 'line',
            data: [],
            lineStyle: {
              width: 0
            },
            symbol: 'none',
            showInLegend: false
          },
          {
            name: '夜间流量平均值',
            type: 'line',
            data: [],
            itemStyle: {
              color: '#0FD0D8'
            },
            lineStyle: {
              width: 2,
              color: '#0FD0D8'
            }
          },
          {
            name: '基线',
            type: 'line',
            data: [],
            itemStyle: {
              color: '#4D6AED'
            },
            lineStyle: {
              type: 'dashed',
              width: 2,
              color: '#4D6AED'
            },
            symbol: 'none' // 不显示数据点
          },
          {
            name: '突发告警',
            type: 'scatter',
            data: [],
            symbolSize: 10,
            itemStyle: {
              color: '#FC0000'
            }
          },
          {
            name: '趋势预警',
            type: 'scatter',
            data: [],
            symbolSize: 10,
            itemStyle: {
              color: '#FF8612'
            }
          },
          {
            name: '数据中断',
            type: 'scatter',
            data: [],
            symbolSize: 10,
            itemStyle: {
              color: '#9e9e9e'
            }
          },
          {
            name: '部分仪表数据缺失',
            type: 'scatter',
            data: [],
            symbolSize: 8,
            symbol: 'circle',
            itemStyle: {
              color: '#FFFFFF',
              borderColor: '#FC0000',
              borderWidth: 2
            }
          }
        ]
      }

      this.dualTrackChartInstance.setOption(option)

      // 响应式调整
      window.addEventListener('resize', () => {
        if (this.dualTrackChartInstance) {
          this.dualTrackChartInstance.resize()
        }
      })
    },

    queryDualTrackData() {
      if (!this.dualTrackQuery.zoneId) {
        this.$message.warning('请选择分区')
        return
      }

      if (!this.dualTrackQuery.timeRange || this.dualTrackQuery.timeRange.length === 0) {
        this.$message.warning('请选择查询时间范围')
        return
      }

      const params = {
        zoneId: this.dualTrackQuery.zoneId,
        startTime: this.dualTrackQuery.timeRange[0].format('YYYY-MM-DD'),
        endTime: this.dualTrackQuery.timeRange[1].format('YYYY-MM-DD')
      }

      this.$http
        .get('/pipe-network/remote_water_meter_alarm_messages/alarm/base/line/zoneid', {
          params: params
        })
        .then(({ data }) => {
          if (data && data.length > 0) {
            this.dualTrackData = data
            this.renderDualTrackChart(data)
            this.generateTableData(data)
          } else {
            this.$message.info('暂无数据')
            this.dualTrackData = []
            this.dualTrackTableData = []
            this.renderDualTrackChart([]) // 显式调用以清空图表
          }
        })
        .catch((error) => {
          console.error('查询双轨预警数据失败:', error)
          this.$message.error('查询失败，请重试')
        })
    },

    renderDualTrackChart(data) {
      if (!this.dualTrackChartInstance) return

      const chartData = data || []

      // 处理数据
      const dates = []
      const actualFlow = []
      const baseLine = []
      const rangeLower = [] // 正常范围下界
      const rangeUpper = [] // 正常范围上界
      const emergencyAlarms = []
      const trendWarnings = []

      const dataInterruptions = [] // 数据中断
      const dataMissing = [] // 部分仪表数据缺失
      const alarmIndexSet = new Set() // 记录有告警的索引，用于隐藏夜间流量平均值的数据点

      // 用于计算 Y 轴范围的所有值
      const allValues = []

      chartData.forEach((item, index) => {
        // 格式化日期
        const date = item.localDate
        const dateStr = `${date.year}/${String(date.monthValue).padStart(2, '0')}/${String(date.dayOfMonth).padStart(
          2,
          '0'
        )}`
        dates.push(dateStr)

        // 夜间流量平均值
        const nightAvgValue = parseFloat(item.nightAvg) || 0
        actualFlow.push(nightAvgValue)
        allValues.push(nightAvgValue)

        // 动态基线
        const baseLineValue = parseFloat(item.baseLine) || 0
        baseLine.push(baseLineValue)
        allValues.push(baseLineValue)

        // 正常范围：分别存储上下界，支持负值
        const upperWater = parseFloat(item.upperWaterDistinction) || 0
        const floorWater = parseFloat(item.floorWaterDistinction) || 0

        rangeLower.push(floorWater)
        rangeUpper.push(upperWater)
        allValues.push(upperWater)
        allValues.push(floorWater)

        // 部分仪表数据缺失（只要 dateMissing 为 true 就显示，不隐藏夜间流量平均值的点）
        if (item.dateMissing) {
          dataMissing.push([index, nightAvgValue])
        }

        // 突发告警（aramNum: 0 表示突发报警）
        if (item.aramNum === 0) {
          emergencyAlarms.push([index, nightAvgValue])
          alarmIndexSet.add(index)
        }

        // 趋势预警（aramNum: 1 表示趋势预警）
        if (item.aramNum === 1) {
          trendWarnings.push([index, nightAvgValue])
          alarmIndexSet.add(index)
        }

        // 数据中断（aramNum: 2 表示数据中断）
        if (item.aramNum === 2) {
          dataInterruptions.push([index, nightAvgValue])
          alarmIndexSet.add(index)
        }
      })

      // 将夜间流量平均值数据转换为带样式的格式，在告警点隐藏数据点符号
      const actualFlowWithStyle = actualFlow.map((value, index) => {
        if (alarmIndexSet.has(index)) {
          return {
            value: value,
            symbol: 'none'
          }
        }
        return value
      })

      // 计算 Y 轴固定范围（基于全部数据，支持负值）
      // 当负数幅度远大于正数时，为正数区域增加更多显示空间，避免正数被压缩
      let yAxisMin = 0
      let yAxisMax = 100
      if (allValues.length > 0) {
        const minVal = Math.min(...allValues)
        const maxVal = Math.max(...allValues)
        const range = maxVal - minVal
        const basePadding = range * 0.1 || 10
        yAxisMin = Math.floor(minVal - basePadding)
        yAxisMax = Math.ceil(maxVal + basePadding)
        // 负数大、正数小时，给正数区域额外空间，使正数波动更易辨认
        if (minVal < 0 && maxVal >= 0 && Math.abs(minVal) > maxVal * 1.5) {
          const asymmetry = Math.abs(minVal) - maxVal
          const extraTopPadding = asymmetry * 0.5
          yAxisMax = Math.ceil(maxVal + basePadding + extraTopPadding)
        }
      }

      const option = {
        title: {
          text: `${this.currentZoneName || ''}夜间流量分析（双轨预警算法beta）`,
          left: 'center',
          top: 0,
          textStyle: {
            fontSize: 16,
            fontWeight: 'bold',
            color: '#333'
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          formatter: (params) => {
            let result = `${params[0].axisValue}<br/>`
            const dataIndex = params[0].dataIndex
            const currentItem = chartData[dataIndex] || {}
            const nightTotalFlow =
              (parseFloat(currentItem.three) || 0) +
              (parseFloat(currentItem.four) || 0) +
              (parseFloat(currentItem.five) || 0)

            result += `&nbsp;&nbsp;&nbsp;夜间总流量: ${nightTotalFlow}<br/>`

            // 用于存储已显示的系列名称，避免重复
            const displayedSeries = new Set()

            params.forEach((param) => {
              // 跳过辅助系列和已显示的系列
              if (
                param.seriesName === '正常范围边界' ||
                param.seriesName === '正常范围边界上' ||
                displayedSeries.has(param.seriesName) ||
                param.value === undefined ||
                param.value === ''
              ) {
                return
              }
              
              displayedSeries.add(param.seriesName)
              
              // 正常范围特殊处理
              if (param.seriesName === '正常范围') {
                const lower = rangeLower[param.dataIndex]
                const upper = rangeUpper[param.dataIndex]
                const customMarker =
                  '<span style="display:inline-block;margin-right:4px;border-radius:50%;width:10px;height:10px;background:rgba(46, 231, 239, 0.8);"></span>'
                result += `${customMarker}${param.seriesName}: ${lower} ~ ${upper}<br/>`
              } else if (param.seriesName === '部分仪表数据缺失') {
                const customMarker =
                  '<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;border:2px solid #FC0000;background:#FFFFFF;"></span>'
                result += `${customMarker}${param.seriesName}<br/>`
              } else if (
                param.seriesName === '突发告警' ||
                param.seriesName === '趋势预警' ||
                param.seriesName === '数据中断'
              ) {
                result += `${param.marker}${param.seriesName}<br/>`
              } else {
                result += `${param.marker}${param.seriesName}: ${param.value}<br/>`
              }
            })
            return result
          }
        },
        legend: {
          data: [
            '夜间流量平均值',
            '基线',
            {
              name: '正常范围',
              icon: 'circle',
              itemStyle: {
                color: 'rgba(46, 231, 239, 0.8)'
              }
            },
            '突发告警',
            '趋势预警',
            '数据中断',
            {
              name: '部分仪表数据缺失',
              icon: 'circle',
              itemStyle: {
                color: '#FFFFFF',
                borderColor: '#FC0000',
                borderWidth: 1
              }
            }
          ],
          top: 40,
          right: 'center',
          itemGap: 25,
          itemWidth: 10,
          itemHeight: 10,
          textStyle: {
            fontSize: 14
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%', // 增加底部空间，为滑块留出位置
          top: '18%',
          containLabel: true
        },
        dataZoom: [
          {
            type: 'slider', // 滑块型数据区域缩放组件
            show: true,
            xAxisIndex: [0],
            start: 0, // 初始显示范围的起始百分比
            end: 100, // 初始显示范围的结束百分比
            bottom: 20, // 距离底部的距离
            height: 20, // 滑块高度
            handleSize: '80%', // 控制手柄大小
            handleStyle: {
              color: '#fff',
              shadowBlur: 3,
              shadowColor: 'rgba(0, 0, 0, 0.6)',
              shadowOffsetX: 2,
              shadowOffsetY: 2
            },
            textStyle: {
              color: '#666'
            },
            borderColor: '#ccc'
          },
          {
            type: 'inside', // 内置型数据区域缩放组件
            xAxisIndex: [0],
            start: 0,
            end: 100,
            zoomOnMouseWheel: true, // 鼠标滚轮缩放
            moveOnMouseMove: true, // 鼠标移动平移
            moveOnMouseWheel: false // 鼠标滚轮平移（关闭，避免与缩放冲突）
          }
        ],
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLabel: {
            formatter: '{value}',
            rotate: 30
          }
        },
        yAxis: {
          type: 'value',
          name: '流量（m³）',
          nameLocation: 'center',
          nameGap: 40,
          min: yAxisMin,
          max: yAxisMax,
          axisLabel: {
            formatter: '{value}'
          }
        },
        series: [
          {
            name: '正常范围',
            type: 'custom',
            renderItem: (params, api) => {
              const xValue = api.value(0)
              const lowerValue = api.value(1)
              const upperValue = api.value(2)
              
              const point1 = api.coord([xValue, lowerValue])
              const point2 = api.coord([xValue, upperValue])
              
              // 获取相邻点以形成多边形
              const nextIndex = params.dataIndex + 1
              if (nextIndex >= rangeLower.length) {
                return null
              }
              
              const nextXValue = nextIndex
              const nextLowerValue = rangeLower[nextIndex]
              const nextUpperValue = rangeUpper[nextIndex]
              
              const point3 = api.coord([nextXValue, nextUpperValue])
              const point4 = api.coord([nextXValue, nextLowerValue])
              
              return {
                type: 'polygon',
                shape: {
                  points: [point1, point2, point3, point4]
                },
                style: {
                  fill: 'rgba(46, 231, 239, 0.3)',
                  stroke: 'transparent',
                  lineWidth: 0
                },
                z: 1
              }
            },
            data: dates.map((date, idx) => [idx, rangeLower[idx], rangeUpper[idx]]),
            z: 1
          },
          {
            name: '正常范围边界',
            type: 'line',
            data: rangeLower,
            lineStyle: {
              width: 0
            },
            symbol: 'none',
            smooth: true,
            z: 2,
            showInLegend: false
          },
          {
            name: '正常范围边界上',
            type: 'line',
            data: rangeUpper,
            lineStyle: {
              width: 0
            },
            symbol: 'none',
            smooth: true,
            z: 2,
            showInLegend: false
          },
          {
            name: '夜间流量平均值',
            type: 'line',
            data: actualFlowWithStyle,
            itemStyle: {
              color: '#0FD0D8'
            },
            lineStyle: {
              width: 2,
              color: '#0FD0D8'
            },
            smooth: true
          },
          {
            name: '基线',
            type: 'line',
            data: baseLine,
            itemStyle: {
              color: '#4D6AED'
            },
            lineStyle: {
              type: 'dashed',
              width: 2,
              color: '#4D6AED'
            },
            symbol: 'none', // 不显示数据点
            smooth: true
          },
          {
            name: '突发告警',
            type: 'scatter',
            data: emergencyAlarms,
            symbolSize: 10,
            itemStyle: {
              color: '#FC0000'
            },
            zlevel: 10
          },
          {
            name: '趋势预警',
            type: 'scatter',
            data: trendWarnings,
            symbolSize: 10,
            itemStyle: {
              color: '#FF8612'
            },
            zlevel: 10
          },
          {
            name: '数据中断',
            type: 'scatter',
            data: dataInterruptions,
            symbolSize: 10,
            itemStyle: {
              color: '#9e9e9e'
            },
            zlevel: 10
          },
          {
            name: '部分仪表数据缺失',
            type: 'scatter',
            data: dataMissing,
            symbolSize: 8,
            symbol: 'circle',
            itemStyle: {
              color: '#FFFFFF',
              borderColor: '#FC0000',
              borderWidth: 2
            },
            zlevel: 10
          }
        ]
      }

      this.dualTrackChartInstance.setOption(option)
    },

    exportDualTrackChart() {
      if (!this.dualTrackChartInstance) {
        this.$message.warning('请先查询数据')
        return
      }

      // 确保标题包含最新的片区名称
      const titleText = `${this.currentZoneName || ''}夜间流量分析（双轨预警算法beta）`
      this.dualTrackChartInstance.setOption({
        title: {
          text: titleText,
          left: 'center',
          top: 0,
          textStyle: {
            fontSize: 16,
            fontWeight: 'bold',
            color: '#333'
          }
        }
      })

      const url = this.dualTrackChartInstance.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      })

      const link = document.createElement('a')
      link.download = `${this.currentZoneName || ''}夜间流量分析（双轨预警算法beta）.png`
      link.href = url
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      this.$message.success('图表导出成功')
    },

    generateTableData(data) {
      this.dualTrackTableData = data.map((item, index) => {
        const date = item.localDate
        const dateStr = `${date.year}-${String(date.monthValue).padStart(2, '0')}-${String(date.dayOfMonth).padStart(
          2,
          '0'
        )}`

        return {
          key: index,
          index: index + 1,
          date: dateStr,
          three: item.three || '-',
          four: item.four || '-',
          five: item.five || '-',
          nightAvg: item.nightAvg || '-',
          baseLine: item.baseLine || '-',
          aramNum: item.aramNum,
          dateMissing: item.dateMissing,
          aramMessage: item.aramMessage || '-'
        }
      })
    },

    handleDisplayModeChange() {
      if (this.displayMode === 'chart') {
        this.$nextTick(() => {
          if (this.dualTrackChartInstance) {
            this.dualTrackChartInstance.resize()
          }
        })
      }
    },

    toggleDisplayMode() {
      this.displayMode = this.displayMode === 'chart' ? 'table' : 'chart'
      this.handleDisplayModeChange()
    },

    exportDualTrackTable() {
      if (!this.dualTrackTableData || this.dualTrackTableData.length === 0) {
        this.$message.warning('暂无数据可导出')
        return
      }

      // 优先尝试使用 xlsx-js-style（支持样式），如果失败则使用标准 xlsx
      Promise.all([import('xlsx-js-style').catch(() => null), import('xlsx').catch(() => null)])
        .then(([XLSXStyle, XLSX]) => {
          // 优先使用支持样式的版本
          const XLSXLib = XLSXStyle || XLSX
          if (!XLSXLib) {
            this.$message.error('无法加载 xlsx 库，请检查依赖')
            return
          }

          const hasStyleSupport = !!XLSXStyle
          // 准备表格数据和报警类型映射
          const tableData = this.dualTrackTableData.map((item) => {
            let alarmTypeText = '-'
            if (item.dateMissing) {
              if (item.aramNum === 0) {
                alarmTypeText = '突发告警 部分仪表数据缺失 '
              } else if (item.aramNum === 1) {
                alarmTypeText = '趋势预警 部分仪表数据缺失'
              } else if (item.aramNum === 2) {
                alarmTypeText = '数据中断 部分仪表数据缺失'
              } else {
                alarmTypeText = '部分仪表数据缺失'
              }
            } else if (item.aramNum === 0) {
              alarmTypeText = '突发告警'
            } else if (item.aramNum === 1) {
              alarmTypeText = '趋势预警'
            } else if (item.aramNum === 2) {
              alarmTypeText = '数据中断'
            }

            return {
              序号: item.index,
              日期: item.date,
              '3点': item.three,
              '4点': item.four,
              '5点': item.five,
              夜间流量平均值: item.nightAvg,
              基线: item.baseLine,
              报警识别: alarmTypeText
            }
          })

          // 保存报警类型映射，用于后续设置颜色（包含 dateMissing 信息）
          const alarmTypeMap = this.dualTrackTableData.map((item) => ({
            aramNum: item.aramNum,
            dateMissing: item.dateMissing
          }))

          // 创建工作表
          const ws = XLSXLib.utils.json_to_sheet(tableData)

          // 设置列宽（报警识别列加宽以容纳多标签文案）
          ws['!cols'] = [
            { wch: 6 },   // 序号
            { wch: 12 },  // 日期
            { wch: 8 },   // 3点
            { wch: 8 },   // 4点
            { wch: 8 },   // 5点
            { wch: 12 },  // 夜间流量平均值
            { wch: 10 },  // 基线
            { wch: 28 }   // 报警识别
          ]

          // 如果支持样式，设置单元格颜色
          if (hasStyleSupport) {
            try {
              const range = XLSXLib.utils.decode_range(ws['!ref'])
              const alarmTypeColIndex = 7 // "报警识别"是第8列（索引从0开始，所以是7）

              // 获取对应行的颜色
              const getRowColor = (alarmInfo) => {
                if (alarmInfo.dateMissing) {
                  return 'FFFC0000' // 部分仪表数据缺失 - 浅红色背景
                } else if (alarmInfo.aramNum === 0) {
                  return 'FFFC0000' // 突发告警 - 红色
                } else if (alarmInfo.aramNum === 1) {
                  return 'FFFF8612' // 趋势预警 - 橙色
                } else if (alarmInfo.aramNum === 2) {
                  return 'FF9E9E9E' // 数据中断 - 灰色
                }
                return null
              }

              // 为整行设置背景颜色（从序号到报警识别，共8列：0-7）
              for (let row = 1; row <= range.e.r; row++) {
                // 从第2行开始（第1行是表头）
                const alarmInfo = alarmTypeMap[row - 1]
                const rowColor = getRowColor(alarmInfo)
                if (rowColor) {
                  // 从序号列（0）到报警识别列（7），整行都设置颜色
                  for (let col = 0; col <= alarmTypeColIndex; col++) {
                    const cellAddress = XLSXLib.utils.encode_cell({ r: row, c: col })
                    // 如果单元格不存在，创建一个空单元格
                    if (!ws[cellAddress]) {
                      ws[cellAddress] = { v: '', t: 's' }
                    }

                    // 初始化样式对象
                    if (!ws[cellAddress].s) {
                      ws[cellAddress].s = {}
                    }

                    // 设置背景颜色
                    ws[cellAddress].s.fill = {
                      fgColor: { rgb: rowColor },
                      patternType: 'solid'
                    }

                    // 设置文字颜色（使用白色）
                    // const isDataMissing = alarmInfo.dateMissing
                    ws[cellAddress].s.font = {
                      color: { rgb: 'FFFFFFFF' },
                      bold: true
                    }

                    // 设置对齐方式
                    ws[cellAddress].s.alignment = {
                      horizontal: 'center',
                      vertical: 'center'
                    }
                  }
                }
              }
            } catch (error) {
              console.warn('样式设置失败:', error)
            }
          } else {
            // 如果不支持样式，提示用户安装 xlsx-js-style
            console.warn('当前使用的 xlsx 库不支持样式，请安装 xlsx-js-style 以支持颜色导出')
            this.$message.warning('当前 xlsx 库不支持样式，导出的文件将无颜色。请安装 xlsx-js-style 库以支持颜色导出')
          }

          // 创建工作簿
          const wb = XLSXLib.utils.book_new()
          XLSXLib.utils.book_append_sheet(wb, ws, '双轨预警数据')

          // 导出文件
          const fileName = `${this.currentZoneName || ''}夜间流量分析.xlsx`
          XLSXLib.writeFile(wb, fileName)

          this.$message.success('列表导出成功')
        })
        .catch((error) => {
          console.error('导出失败:', error)
          // 如果 xlsx 库未安装，使用 CSV 导出作为备选方案
          this.exportDualTrackTableAsCSV()
        })
    },

    exportDualTrackTableAsCSV() {
      if (!this.dualTrackTableData || this.dualTrackTableData.length === 0) {
        this.$message.warning('暂无数据可导出')
        return
      }

      // 准备 CSV 数据
      let csvContent = '\uFEFF' // UTF-8 BOM，确保中文正常显示
      csvContent += '序号,日期,3点,4点,5点,夜间流量平均值,基线,报警识别\n'

      this.dualTrackTableData.forEach((item) => {
        let alarmTypeText = '-'
        if (item.dateMissing) {
          if (item.aramNum === 0) {
            alarmTypeText = '突发告警 部分仪表数据缺失 '
          } else if (item.aramNum === 1) {
            alarmTypeText = '趋势预警 部分仪表数据缺失'
          } else if (item.aramNum === 2) {
            alarmTypeText = '数据中断 部分仪表数据缺失'
          } else {
            alarmTypeText = '部分仪表数据缺失'
          }
        } else if (item.aramNum === 0) {
          alarmTypeText = '突发告警'
        } else if (item.aramNum === 1) {
          alarmTypeText = '趋势预警'
        } else if (item.aramNum === 2) {
          alarmTypeText = '数据中断'
        }

        csvContent += `${item.index},${item.date},${item.three},${item.four},${item.five},${item.nightAvg},${item.baseLine},${alarmTypeText}\n`
      })

      // 创建 Blob 并下载
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)

      link.setAttribute('href', url)
      link.setAttribute('download', `${this.currentZoneName || ''}夜间流量分析.csv`)
      link.style.visibility = 'hidden'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      this.$message.success('列表导出成功')
    }
  }
}
</script>

<style lang="less" scoped>
.dual-track-container {
  background-color: #f0f2f5;
  min-height: 100vh;

  .header-card {
    margin-bottom: 16px;
    border-radius: 4px;

    .header-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    .page-title {
      font-size: 20px;
      font-weight: 600;
      color: #262626;
      margin-bottom: 12px;
      display: flex;
      align-items: center;

      .beta-tag {
        font-size: 12px;
        background: #e6f7ff;
        color: #1890ff;
        padding: 0 8px;
        border-radius: 10px;
        margin-left: 8px;
        font-weight: normal;
      }
    }

    .btn-back {
      padding: 0;
      height: auto;
      color: #1890ff;
    }

    .page-description {
      font-size: 14px;
      color: #8c8c8c;
      line-height: 1.6;
      background: #fafafa;
      padding: 12px 16px;
      border-radius: 4px;
      margin-bottom: 20px; // 与筛选条件之间的间距

      .algorithm-tip-icon {
        margin-left: 4px;
        font-size: 16px;
        color: #FFD1AF;
        cursor: help;
        vertical-align: middle;
        transition: color 0.2s ease;

        &:hover {
          color: #1890ff;
        }
      }
    }

    .filter-wrapper {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 24px;
      padding-top: 16px; // 与描述之间的分隔
      // border-top: 1px solid #f0f0f0; // 添加分隔线

      .filter-item {
        display: flex;
        align-items: center;

        .filter-label {
          font-size: 14px;
          color: #262626;
          margin-right: 8px;
          white-space: nowrap;

          .required {
            color: #ff4d4f;
            margin-right: 4px;
          }
        }
      }

      .filter-actions {
        display: flex;
        gap: 12px;

        .btn-query {
          background: #1890ff;
          border-color: #1890ff;
        }

        .btn-export {
          background: #1890ff;
          border-color: #1890ff;
        }
      }
    }
  }

  .content-card {
    border-radius: 4px;

    .content-header {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      position: relative;
      margin-bottom: 30px;

      .mode-switch {
        position: relative;

        .action-icon-btn {
          font-size: 20px;
          color: #8c8c8c;
          cursor: pointer;
          transition: all 0.3s;

          &:hover {
            color: #1890ff;
          }
        }
      }
    }
  }
}

.dual-track-content {
  .chart-container {
    width: 100%;
    height: 600px;
  }

  .alarm-type-cell {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;

    .tag-data-missing {
      background: #ffffff !important;
      border: 1px solid #fc0000;
      color: #fc0000 !important;
    }
  }
}
</style>
